import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon, Users, MapPin, ChevronRight, Check, Loader2, BedDouble, ArrowLeft, Plus, Minus, Baby } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n || 0);

export default function BookingEnginePage() {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState(null);

  // Steps: 1=dates, 2=rooms, 3=guest info, 4=confirmation
  const [step, setStep] = useState(1);
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  const [numRooms, setNumRooms] = useState(1);
  const [roomConfigs, setRoomConfigs] = useState([{ adults: 2, children: [] }]);
  // For step 1, show config of first room; total guests for pricing
  const adults = roomConfigs[0]?.adults || 2;
  const children = roomConfigs[0]?.children || [];
  const guests = adults + children.length;
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]); // array of selected rooms (one per numRooms)
  const selectedRoom = selectedRooms[0] || null; // backwards compat
  const [searchingRooms, setSearchingRooms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [currentRoomPick, setCurrentRoomPick] = useState(0); // which room slot we're picking for

  const [guestData, setGuestData] = useState({
    name: '', email: '', phone: '', documento: '', notas: ''
  });

  // Auto-rotate photos
  useEffect(() => {
    if (photos.length <= 1) return;
    const timer = setInterval(() => setPhotoIndex(i => (i + 1) % photos.length), 4000);
    return () => clearInterval(timer);
  }, [photos.length]);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/property/${code}`);
        setProperty(res.data.property);
        setRooms(res.data.rooms);
        setPhotos(res.data.photos || []);
      } catch {
        setError('Propiedad no encontrada');
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [code]);

  // Min dates
  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const minCheckout = useMemo(() => checkin ? format(addDays(new Date(checkin), 1), 'yyyy-MM-dd') : today, [checkin, today]);

  const searchAvailability = async () => {
    if (!checkin || !checkout) { toast.error('Selecciona fechas'); return; }
    setSearchingRooms(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/availability/${code}?checkin=${checkin}&checkout=${checkout}&guests=${guests}&num_rooms=${numRooms}`);
      setAvailableRooms(res.data.available_rooms);
      setSelectedRooms([]);
      setCurrentRoomPick(0);
      setStep(2);
      if (res.data.available_rooms.length === 0) toast.info('No hay habitaciones disponibles para esa cantidad de personas y fechas');
    } catch { toast.error('Error al buscar disponibilidad'); }
    finally { setSearchingRooms(false); }
  };

  const selectRoom = (room) => {
    if (numRooms === 1) {
      setSelectedRooms([room]);
      setStep(3);
    } else {
      // Multi-room: add to selection
      const newSelection = [...selectedRooms];
      newSelection[currentRoomPick] = room;
      setSelectedRooms(newSelection);
      if (currentRoomPick < numRooms - 1) {
        setCurrentRoomPick(currentRoomPick + 1);
        toast.success(`Habitación ${currentRoomPick + 1} seleccionada. Selecciona la habitación ${currentRoomPick + 2}.`);
      } else {
        setStep(3);
      }
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!guestData.name || !guestData.phone) { toast.error('Nombre y teléfono son obligatorios'); return; }
    setSubmitting(true);
    try {
      // Create one reservation per selected room
      const results = [];
      for (const sr of selectedRooms) {
        if (!sr) continue;
        const rc = roomConfigs[selectedRooms.indexOf(sr)] || roomConfigs[0];
        const roomGuests = rc.adults + rc.children.length;
        const res = await axios.post(`${BACKEND_URL}/api/public/booking`, {
          property_code: code,
          room_id: sr.id,
          guest_name: guestData.name,
          guest_email: guestData.email,
          guest_phone: guestData.phone,
          guest_documento: guestData.documento,
          fecha_checkin: checkin,
          fecha_checkout: checkout,
          num_guests: roomGuests,
          num_adults: rc.adults,
          num_children: rc.children.length,
          children_ages: rc.children,
          total: sr.total,
          price_per_night: sr.price_per_night,
          notas: guestData.notas
        });
        results.push(res.data);
      }
      setBookingResult(results[0]);
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear reserva');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="pt-8 text-center">
          <BedDouble className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-700 mb-2">Propiedad no encontrada</h2>
          <p className="text-muted-foreground">El código "{code}" no corresponde a ninguna propiedad activa.</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="booking-engine">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-slate-800 text-white">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }} data-testid="booking-property-name">{property.nombre}</h1>
            {property.ciudad && (
              <div className="flex items-center gap-1 text-slate-300 text-sm mt-1">
                <MapPin className="h-3.5 w-3.5" />
                <span>{property.ciudad}{property.direccion ? ` · ${property.direccion}` : ''}</span>
              </div>
            )}
          </div>
          <Badge className="bg-white/10 text-white border border-white/20 font-mono" data-testid="booking-prop-code">{property.codigo_unico}</Badge>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Photo Gallery */}
        {photos.length > 0 && step < 4 && (
          <div className="mb-8 rounded-2xl overflow-hidden shadow-lg relative h-[320px] bg-slate-200" data-testid="booking-photos">
            {photos.map((photo, i) => (
              <img
                key={photo.id || i}
                src={photo.url?.startsWith('data:') ? photo.url : `${BACKEND_URL}${photo.url}`}
                alt={photo.caption || property.nombre}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === photoIndex ? 'opacity-100' : 'opacity-0'}`}
              />
            ))}
            {photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {photos.map((_, i) => (
                  <button key={i} onClick={() => setPhotoIndex(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === photoIndex ? 'bg-white scale-110' : 'bg-white/50'}`} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Progress Steps */}
        {step < 4 && (
          <div className="flex items-center justify-center gap-2 mb-8" data-testid="booking-steps">
            {[
              { n: 1, label: 'Fechas' },
              { n: 2, label: 'Habitación' },
              { n: 3, label: 'Datos' },
            ].map((s, i) => (
              <React.Fragment key={s.n}>
                {i > 0 && <div className={`w-8 h-0.5 ${step >= s.n ? 'bg-slate-700' : 'bg-slate-200'}`} />}
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  step === s.n ? 'bg-slate-800 text-white' :
                  step > s.n ? 'bg-slate-200 text-slate-700' : 'bg-slate-100 text-slate-400'
                }`}>
                  {step > s.n ? <Check className="h-3.5 w-3.5" /> : null}
                  {s.label}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step 1: Select Dates */}
        {step === 1 && (
          <Card className="max-w-lg mx-auto shadow-md" data-testid="booking-step-dates">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <CalendarIcon className="h-5 w-5 text-slate-600" />
                Selecciona tus fechas y huéspedes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Check-in</Label>
                  <Input type="date" min={today} value={checkin} onChange={e => { setCheckin(e.target.value); if (checkout && e.target.value >= checkout) setCheckout(''); }} data-testid="booking-checkin" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Check-out</Label>
                  <Input type="date" min={minCheckout} value={checkout} onChange={e => setCheckout(e.target.value)} data-testid="booking-checkout" />
                </div>
              </div>

              {/* Number of rooms */}
              <div className="space-y-1">
                <Label className="text-xs font-medium flex items-center gap-1.5">
                  <BedDouble className="h-3.5 w-3.5" /> Habitaciones
                </Label>
                <div className="flex items-center gap-3">
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => { const n = Math.max(1, numRooms - 1); setNumRooms(n); setRoomConfigs(prev => prev.slice(0, n)); }} disabled={numRooms <= 1} data-testid="booking-rooms-minus">
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="text-lg font-bold w-8 text-center" data-testid="booking-rooms-count">{numRooms}</span>
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => { const n = Math.min(10, numRooms + 1); setNumRooms(n); setRoomConfigs(prev => [...prev, { adults: 2, children: [] }]); }} disabled={numRooms >= 10} data-testid="booking-rooms-plus">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Per-room occupant config */}
              {roomConfigs.map((rc, idx) => (
                <div key={idx} className={`space-y-3 ${numRooms > 1 ? 'border rounded-lg p-3 bg-slate-50/50' : ''}`}>
                  {numRooms > 1 && <div className="text-xs font-semibold text-slate-500">Habitación {idx + 1}</div>}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Adultos</Label>
                    <div className="flex items-center gap-3">
                      <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setRoomConfigs(prev => prev.map((r, i) => i === idx ? { ...r, adults: Math.max(1, r.adults - 1) } : r))} disabled={rc.adults <= 1} data-testid="booking-adults-minus"><Minus className="h-4 w-4" /></Button>
                      <span className="text-lg font-bold w-8 text-center" data-testid="booking-adults-count">{rc.adults}</span>
                      <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setRoomConfigs(prev => prev.map((r, i) => i === idx ? { ...r, adults: Math.min(10, r.adults + 1) } : r))} disabled={rc.adults >= 10} data-testid="booking-adults-plus"><Plus className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium flex items-center gap-1.5"><Baby className="h-3.5 w-3.5" /> Menores de edad</Label>
                    <div className="flex items-center gap-3">
                      <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setRoomConfigs(prev => prev.map((r, i) => i === idx ? { ...r, children: r.children.slice(0, -1) } : r))} disabled={rc.children.length === 0} data-testid="booking-children-minus"><Minus className="h-4 w-4" /></Button>
                      <span className="text-lg font-bold w-8 text-center" data-testid="booking-children-count">{rc.children.length}</span>
                      <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setRoomConfigs(prev => prev.map((r, i) => i === idx ? { ...r, children: [...r.children, 5] } : r))} disabled={rc.children.length >= 6} data-testid="booking-children-plus"><Plus className="h-4 w-4" /></Button>
                    </div>
                    {rc.children.length > 0 && (
                      <div className="space-y-2 mt-2 pl-1">
                        {rc.children.map((age, cidx) => (
                          <div key={cidx} className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-20">Menor {cidx + 1}:</span>
                            <Input type="number" min={0} max={17} value={age} onChange={e => { const val = Math.max(0, Math.min(17, parseInt(e.target.value) || 0)); setRoomConfigs(prev => prev.map((r, i) => i === idx ? { ...r, children: r.children.map((a, ci) => ci === cidx ? val : a) } : r)); }} className="w-20 h-8 text-center text-sm" data-testid={`booking-child-age-${cidx}`} />
                            <span className="text-xs text-muted-foreground">años</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <Button onClick={searchAvailability} disabled={!checkin || !checkout || searchingRooms} className="w-full bg-slate-800 hover:bg-slate-700 h-12 text-base" data-testid="booking-search-btn">
                {searchingRooms ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <CalendarIcon className="h-5 w-5 mr-2" />}
                Buscar Disponibilidad
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Room */}
        {step === 2 && (
          <div className="space-y-4" data-testid="booking-step-rooms">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  {numRooms > 1 ? `Selecciona habitación ${currentRoomPick + 1} de ${numRooms}` : 'Habitaciones disponibles'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(checkin), "d 'de' MMMM", { locale: es })} — {format(new Date(checkout), "d 'de' MMMM yyyy", { locale: es })} · {adults} adulto{adults > 1 ? 's' : ''}{children.length > 0 ? `, ${children.length} menor${children.length > 1 ? 'es' : ''}` : ''}{numRooms > 1 ? ` · ${numRooms} habitaciones` : ''}
                </p>
                {numRooms > 1 && selectedRooms.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedRooms.map((sr, i) => sr && (
                      <span key={i} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Hab. {i+1}: {sr.nombre}</span>
                    ))}
                  </div>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => setStep(1)} data-testid="booking-back-dates">
                <ArrowLeft className="h-4 w-4 mr-1" /> Cambiar fechas
              </Button>
            </div>

            {availableRooms.length === 0 ? (
              <Card className="py-12 text-center">
                <BedDouble className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-lg font-medium text-slate-500">No hay habitaciones disponibles</p>
                <p className="text-sm text-muted-foreground mt-1">Intenta con otras fechas</p>
                <Button variant="outline" className="mt-4" onClick={() => setStep(1)}>Cambiar fechas</Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {availableRooms.map(room => {
                  const alreadySelected = selectedRooms.some(sr => sr?.id === room.id);
                  return (
                  <Card key={room.id} className={`overflow-hidden transition-shadow cursor-pointer border-2 ${alreadySelected ? 'border-green-400 opacity-60' : 'border-transparent hover:border-slate-300 hover:shadow-lg'}`} onClick={() => !alreadySelected && selectRoom(room)} data-testid={`booking-room-${room.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>{room.nombre}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />{room.tipo}</span>
                            <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />Hasta {room.capacidad}p</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-muted-foreground">{room.num_nights} noche{room.num_nights > 1 ? 's' : ''}</div>
                          <div className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>{fmt(room.total)}</div>
                          <div className="text-xs text-muted-foreground">{fmt(room.price_per_night)}/noche</div>
                        </div>
                      </div>
                      <Button className="w-full mt-4 bg-slate-800 hover:bg-slate-700" disabled={alreadySelected}>
                        {alreadySelected ? 'Ya seleccionada' : 'Seleccionar'} <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Guest Info */}
        {step === 3 && selectedRooms.length > 0 && (
          <div className="max-w-2xl mx-auto" data-testid="booking-step-guest">
            <Button variant="outline" size="sm" onClick={() => { setStep(2); setCurrentRoomPick(numRooms > 1 ? numRooms - 1 : 0); }} className="mb-4" data-testid="booking-back-rooms">
              <ArrowLeft className="h-4 w-4 mr-1" /> Volver a habitaciones
            </Button>

            <div className="grid md:grid-cols-5 gap-6">
              {/* Summary sidebar */}
              <Card className="md:col-span-2 h-fit bg-slate-800 text-white border-none">
                <CardContent className="pt-5 space-y-3">
                  <h3 className="font-bold" style={{ fontFamily: 'Manrope' }}>Resumen</h3>
                  <Separator className="bg-white/20" />
                  <div className="text-sm space-y-2">
                    {selectedRooms.map((sr, i) => sr && (
                      <div key={i} className="flex justify-between"><span className="text-slate-300">{numRooms > 1 ? `Hab. ${i+1}` : 'Habitación'}</span><span className="font-medium">{sr.nombre}</span></div>
                    ))}
                    <div className="flex justify-between"><span className="text-slate-300">Check-in</span><span>{format(new Date(checkin), "dd/MM/yyyy")}</span></div>
                    <div className="flex justify-between"><span className="text-slate-300">Check-out</span><span>{format(new Date(checkout), "dd/MM/yyyy")}</span></div>
                    <div className="flex justify-between"><span className="text-slate-300">Noches</span><span>{selectedRooms[0]?.num_nights}</span></div>
                    <div className="flex justify-between"><span className="text-slate-300">Adultos</span><span>{adults}</span></div>
                    {children.length > 0 && (
                      <div className="flex justify-between"><span className="text-slate-300">Menores</span><span>{children.length} ({children.map(a => `${a}a`).join(', ')})</span></div>
                    )}
                  </div>
                  <Separator className="bg-white/20" />
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300 font-medium">Total</span>
                    <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{fmt(selectedRooms.reduce((sum, sr) => sum + (sr?.total || 0), 0))}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Guest form */}
              <Card className="md:col-span-3">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Datos del huésped</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={submitBooking} className="space-y-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Nombre completo *</Label>
                      <Input value={guestData.name} onChange={e => setGuestData({ ...guestData, name: e.target.value })} placeholder="Juan Pérez" required data-testid="booking-guest-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Email</Label>
                        <Input type="email" value={guestData.email} onChange={e => setGuestData({ ...guestData, email: e.target.value })} placeholder="juan@email.com" data-testid="booking-guest-email" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Teléfono *</Label>
                        <Input value={guestData.phone} onChange={e => setGuestData({ ...guestData, phone: e.target.value })} placeholder="+57 300 123 4567" required data-testid="booking-guest-phone" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Documento de identidad</Label>
                      <Input value={guestData.documento} onChange={e => setGuestData({ ...guestData, documento: e.target.value })} placeholder="CC 1234567890" data-testid="booking-guest-doc" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Notas o solicitudes especiales</Label>
                      <Input value={guestData.notas} onChange={e => setGuestData({ ...guestData, notas: e.target.value })} placeholder="Llegamos tarde, necesitamos cuna..." data-testid="booking-guest-notes" />
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-slate-800 hover:bg-slate-700 h-12 text-base" data-testid="booking-submit-btn">
                      {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Check className="h-5 w-5 mr-2" />}
                      Confirmar Reserva
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">No se requiere pago. La reserva quedará pendiente de confirmación.</p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && bookingResult && (
          <Card className="max-w-md mx-auto text-center shadow-lg" data-testid="booking-step-confirm">
            <CardContent className="pt-10 pb-8 space-y-4">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto">
                <Check className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Reserva Registrada</h2>
              <p className="text-muted-foreground">Tu reserva ha sido registrada exitosamente y está pendiente de confirmación.</p>
              <Separator />
              <div className="text-sm space-y-2 text-left bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between"><span className="text-muted-foreground">Propiedad</span><span className="font-medium">{property.nombre}</span></div>
                {selectedRooms.map((sr, i) => sr && (
                  <div key={i} className="flex justify-between"><span className="text-muted-foreground">{numRooms > 1 ? `Habitación ${i+1}` : 'Habitación'}</span><span className="font-medium">{sr.nombre}</span></div>
                ))}
                <div className="flex justify-between"><span className="text-muted-foreground">Fechas</span><span>{format(new Date(checkin), "dd/MM")} - {format(new Date(checkout), "dd/MM/yyyy")}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Adultos</span><span>{adults}</span></div>
                {children.length > 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Menores</span><span>{children.length} ({children.map(a => `${a} años`).join(', ')})</span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-bold text-lg">{fmt(selectedRooms.reduce((sum, sr) => sum + (sr?.total || 0), 0))}</span></div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                Recibirás una confirmación pronto. Si tienes preguntas, contáctanos directamente.
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-slate-800 text-white/60 text-center py-6 mt-16 text-xs">
        Motor de Reservas · {property?.nombre} · Powered by Edén PMS
      </footer>
    </div>
  );
}
